import * as Sentry from '@sentry/react'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useEffectOnce } from 'react-use'
import store from 'store'

import agent from 'agent'
import {
  type AuthContextValues,
  BASE_SESSION,
  DEFAULT_AUTH_VALUES,
  getActiveTeamIdFromLocalStorage,
  getEntitlementsForAuth,
  getOpenApi,
  getPrivilegeForEndpoint,
  getRolesFromLocalStorage,
  getUserSession,
  renewUserSession,
  setActiveTeamIdInLocalStorage,
  TEAM_DETAILS_FALLBACK
} from 'context/auth-context-utils'
import toast from 'lib/toast'
import { useGetRoles } from 'queries/roles/use-get-roles'
import { resetAnalytics } from 'track'
import { trimOpenApiFile } from 'utils/openapi-utils'
import openApiJson from 'utils/openapi.json'
import { getSessionData, NO_TEAM } from 'utils/session'
import { ms } from 'utils/time/millisecond'

import type { OpenApiResponse } from 'types/api/openapi'
import type { RbacBody, RbacPrivilege } from 'types/api/rbac/rbac'
import type {
  UserSession,
  UserSessionTeam
} from 'types/api/users/sessions/user-session'
import type { ApiProp } from 'utils/endpoints-access'

export { DEFAULT_AUTH_VALUES, NO_TEAM }
export const AuthContext = createContext(DEFAULT_AUTH_VALUES)

const SESSION_POLLING_TIME = ms.minutes(10)
const OPENAPI_POLLING_TIME = ms.hours(1)

export const AuthProvider: React.FC = ({ children }) => {
  // TODO: I am pretty sure that our session management implementation deletes this storage
  // key any time the session ends, so this will always be undefined until that is fixed.
  const cachedRoles = useRef(getRolesFromLocalStorage())
  const [
    { session, ready, currentTeamId, isRbacEnabled, openApi, RBAC },
    setAuthState
  ] = useState(() => ({
    currentTeamId: '',
    isRbacEnabled: false,
    openApi: openApiJson as unknown as OpenApiResponse,
    RBAC: cachedRoles.current ?? ({} as RbacBody),
    ready: false,
    session: BASE_SESSION
  }))

  const setOpenApi = useCallback(() => {
    // returns openApiFallback
    return getOpenApi({
      onSuccess: (openApi) => {
        setAuthState((current) => ({
          ...current,
          openApi: trimOpenApiFile(openApi)
        }))
      }
    })
  }, [])

  const endSession = useCallback(async () => {
    if (session.header) {
      try {
        await agent.delete('/users/auth')
      } catch (err) {
        /* We want to wipe out all local storage and log the user out despite the success/failure of the agent.delete('/users/auth') call */
      }

      /**
       * The following action wipes out all local storage persistence without discretion, whereas the
       *  need to persist the user preference state for alert visibility is needed. This single key
       *  is temporarily stored and re-applied following the clear() call.
       */
      const alertPreferencesPersistence = store.get('alertPreferences')
      const redirectPathPersistence = store.get('loginRedirect')
      const lastActiveOrgId = store.get('lastActiveOrgId')
      const rbacSummary = store.get('rbac-summary')
      // we store some user settings in localStorage to survive page navigations
      // so make sure that's cleared out on logout
      window.localStorage.clear()
      // re-applying the user alert preference object
      store.set('alertPreferences', alertPreferencesPersistence)
      store.set('loginRedirect', redirectPathPersistence)
      // persist last team id
      store.set('lastActiveOrgId', lastActiveOrgId)
      store.set('rbac-summary', rbacSummary)
    }

    // clear segment.com analytics data
    resetAnalytics()
    /**
     * TODO: These calls are potentially redundant if followed by the explicit localStorage.clear() call
     * above, and a new design should be considered to persist local data and delete necessary ones.
     */
    store.remove('session')
    store.remove('onboardingAccessed')
    // discard the choice of resetting secret key when user log out
    store.remove('confirmSecretReset')
    store.remove('pendingEnvironmentRequestID')
    setAuthState((current) => ({
      ...current,
      ready: true,
      session: BASE_SESSION,
      currentTeamId: '',
      isRbacEnabled: false
    }))
  }, [session.header, setAuthState])

  const startSession = useCallback(
    async (
      activeSession: UserSession = store.get('session'),
      activeTeamId?: string
    ) => {
      const hasSession = activeSession?.company_id

      activeTeamId =
        activeTeamId ||
        getActiveTeamIdFromLocalStorage(hasSession ?? currentTeamId)

      if (!hasSession) {
        setAuthState((current) => ({
          ...current,
          ready: true
        }))
        return
      }

      const isExpired = new Date(activeSession.expires_at) <= new Date()
      let updatedSession = activeSession
      try {
        if (isExpired) {
          updatedSession = await renewUserSession(activeSession)
        } else {
          updatedSession = await getUserSession(activeSession)
        }
      } catch (e) {
        return endSession()
      }

      const { sessionToStart, activeTeam } = getSessionData(
        updatedSession,
        activeTeamId
      )

      Sentry.setUser({
        email: sessionToStart.identifier,
        id: sessionToStart.identifier
      })
      Sentry.setContext('session', {
        teamId: activeTeam.identifier,
        companyId: activeSession?.company_id
      })

      setAuthState((state) => ({
        ...state,
        currentTeamId: activeTeam.identifier ?? state.currentTeamId,
        ready: true,
        session: sessionToStart,
        openApi: setOpenApi() // to start the session, we use the openApi fallback
      }))

      store.set('session', sessionToStart)

      setActiveTeamIdInLocalStorage(
        activeTeam.identifier,
        updatedSession.company_id
      )
      return sessionToStart
    },
    [currentTeamId, endSession, setOpenApi]
  )

  const renewSession = useCallback(async () => {
    try {
      const nextSession = await startSession(await renewUserSession(session))
      return nextSession
    } catch (err) {
      endSession()
    }
  }, [endSession, session, startSession])

  const fetchSession = useCallback(async () => {
    try {
      const nextSession = await startSession(session)
      return nextSession
    } catch (e) {
      endSession()
    }
  }, [endSession, session, startSession])

  const setActiveTeamId = useCallback(
    async (teamId: string) => {
      setAuthState((current) => ({
        ...current,
        currentTeamId: teamId
      }))

      setActiveTeamIdInLocalStorage(teamId, session.company_id)
    },
    [session.company_id]
  )

  /**
   * Get the session details for the user's team given a teamId
   */
  const getTeamDetails = useCallback(
    (teamId: string) => {
      return (session.teams.find((t) => t.identifier === teamId) ||
        TEAM_DETAILS_FALLBACK) as UserSessionTeam
    },
    [session.teams]
  )

  /**
   * Checks if the given privilege is in the company-wide privileges on a user's session
   */
  const hasCompanyPrivilege = useCallback(
    (privilege: RbacPrivilege) => session.privileges.includes(privilege),
    [session.privileges]
  )

  /**
   * Get the privileges for a user's team session given a teamId
   */
  const privilegesForTeam = useCallback(
    (teamId: string) => {
      return getTeamDetails(teamId).privileges
    },
    [getTeamDetails]
  )

  /**
   * Checks if the given privilege is in the privileges on a user's specified team session.
   * If not, checks hasCompanyPrivilege
   */
  const hasTeamPrivilege = useCallback(
    (teamId: string, privilege: RbacPrivilege) =>
      privilegesForTeam(teamId).includes(privilege) ||
      // Some high level roles can manage teams even if not a member
      hasCompanyPrivilege(privilege),
    [hasCompanyPrivilege, privilegesForTeam]
  )

  /**
   * Given an endpoint and method, checks if the user has access.
   * This is done by checking the user's session for the privilege associated with the endpoint.
   * More details in docs/rbac.md
   *
   * The `teamId` is optional. If it's included, it checks `hasTeamPrivilege`. Otherwise,
   * it checks `hasCompanyPrivilege`. (See the doc-strings on those functions for more details)
   */
  const hasEndpointAccess = useCallback(
    (
      apiProperties: ApiProp,
      options: {
        teamId?: string
      } = {}
    ) => {
      const { teamId } = options

      const privilegeForEndpoint = getPrivilegeForEndpoint(
        apiProperties.endpoint,
        apiProperties.method,
        openApi
      )

      if (!privilegeForEndpoint) {
        return false
      }

      if (teamId) {
        return hasTeamPrivilege(teamId, privilegeForEndpoint)
      }

      return hasCompanyPrivilege(privilegeForEndpoint)
    },
    [openApi, hasCompanyPrivilege, hasTeamPrivilege]
  )

  const hasEndpointAccessForAll = useCallback(
    (properties: ApiProp[], useTeamScope) =>
      properties.every((prop) =>
        hasEndpointAccess(prop, useTeamScope ? { teamId: currentTeamId } : {})
      ),
    [currentTeamId, hasEndpointAccess]
  )

  const authContextValue = useMemo<AuthContextValues>(() => {
    const currentTeam = getTeamDetails(currentTeamId)
    const currentRoles = session.roles.concat(currentTeam.roles)
    const isRunningInCypress = Boolean(window.Cypress)

    return {
      ...getEntitlementsForAuth(session),
      RBAC,
      authHeader: session.header,
      companyDisplayName: session.company_alias ?? session.company_name,
      companyId: session.company_id,
      companyIsAlfiEnabled: isRunningInCypress
        ? session.company_is_alfi_enabled
        : DEFAULT_AUTH_VALUES.companyIsAlfiEnabled, // todo: EN-5114 remove
      companyPrefs: session.prefs,
      companyPrivileges: session.privileges,
      currentTeam,
      currentTeamId,
      currentTeamRoles: currentTeam.roles,
      endSession,
      fetchSession,
      getTeamDetails,
      hasEndpointAccess,
      hasEndpointAccessForAll,
      isProductionEnvironment:
        session?.prefs?.production || currentTeam?.production,
      isRbacEnabled,
      privilegesForTeam,
      ready,
      renewSession,
      roles: currentRoles,
      session,
      setAllAuth: setAuthState,
      setActiveTeamId,
      startSession,
      teams: session.teams
    }
  }, [
    RBAC,
    currentTeamId,
    isRbacEnabled,
    ready,
    session,
    endSession,
    fetchSession,
    getTeamDetails,
    hasEndpointAccess,
    hasEndpointAccessForAll,
    privilegesForTeam,
    renewSession,
    setActiveTeamId,
    startSession
  ])

  useEffectOnce(() => {
    startSession()
  })

  // we need to update the session on a regular basis
  // as the restricted time windows can be updated by another user at any time
  // so we want to be sure that we get those updates quickly to display the warnings
  const sessionInterval = useRef<ReturnType<typeof setInterval>>()
  useEffect(() => {
    if (!sessionInterval.current) {
      sessionInterval.current = setInterval(() => {
        fetchSession()
      }, SESSION_POLLING_TIME)
    }

    return () => {
      clearInterval(sessionInterval.current)
    }
  }, [fetchSession])

  // for security reasons, we want to refresh openapi.json at a regular interval
  const openApiInterval = useRef<ReturnType<typeof setInterval>>()
  useEffect(() => {
    if (!openApiInterval.current) {
      openApiInterval.current = setInterval(() => {
        setOpenApi()
      }, OPENAPI_POLLING_TIME)
    }

    return () => {
      clearInterval(openApiInterval.current)
    }
  }, [setOpenApi])

  /*
   * Handle Roles fetching for the current user. First attempt to fetch roles from our endpoints,
   * and if that fails, attempt to pull data from localStorage. When both scenarios fail, we
   * should log out the user and display an error toast to inform them of the failure, as the
   * webapp cannot reliably function without this data.
   */
  useGetRoles({
    enabled: Boolean(session.identifier),
    onSuccess(data) {
      setAuthState((state) => ({ ...state, RBAC: data, isRbacEnabled: true }))
      // Save a local copy of RbacBody to localStorage
      store.set('rbac-summary', { date: new Date(), summary: data })
    },
    onError() {
      const { current: cachedRbacSummary } = cachedRoles

      if (cachedRbacSummary) {
        setAuthState((state) => ({
          ...state,
          RBAC: cachedRbacSummary,
          isRbacEnabled: true
        }))
        return
      }

      endSession()
      toast({
        type: 'error',
        text: 'There was an issue getting a list of your roles, try logging in again.'
      })
    }
  })

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}
